import { IMembershipUpdateProps } from "@/api_framework/api_modals/Membership";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useMembershipCouponAPI } from "@/app/hooks/api_hooks/Membership/useMembershipCouponAPI";
import { useMembershipDetailsAPI } from "@/app/hooks/api_hooks/Membership/useMembershipDetailsAPI";
import { useMembershipVoucherListingAPI } from "@/app/hooks/api_hooks/Membership/useMembershipVoucherListingAPI";
import { useUpdateMembershipAPI } from "@/app/hooks/api_hooks/Membership/useUpdateMembershipAPI";
import { useSelectMenuReducer as reduceSelectMenu } from "@/app/hooks/useSelectMenuReducer";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

export interface IMembershipAddCouponProps {
  membership_coupons: Array<{ coupon_id: string; coupon_name: string }>;
  membership_other_coupons: Array<{ coupon_id: string; coupon_name: string }>;
  coupon_voucher_mappings: Array<{
    coupon_id: string;
    coupon_name: string;
    voucher_family_id?: string;
  }>;
}

const MembershipAddCouponComponent = () => {
  const navigate = useNavigate();
  const { execute: updateMembership } = useUpdateMembershipAPI();
  const [params, _setParams] = useSearchParams();
  const membership_id = params.get("membership_id");
  const { execute: fetchMembershipDetails, membershipDetails } = useMembershipDetailsAPI();
  const { execute: fetchMembershipCoupon, membershipCouponList } = useMembershipCouponAPI();

  const membershipExhaustibleCouponOptions = [
    ...(membershipCouponList?.exhaustible_coupons?.map((coupon) => ({
      id: coupon.coupon_id,
      title: coupon.coupon_name,
    })) || []),
  ];

  const membershipOtherCouponOptions = [
    ...(membershipCouponList?.other_coupons?.map((coupon) => ({
      id: coupon.coupon_id,
      title: coupon.coupon_name,
    })) || []),
  ];

  const formHook = useForm<IMembershipAddCouponProps>({
    mode: "onChange",
    defaultValues: { coupon_voucher_mappings: [] },
  });

  const { control } = formHook;
  const {
    fields: mappingFields,
    replace: replaceMappings,
  } = useFieldArray({ control, name: "coupon_voucher_mappings" });

  useEffect(() => {
    fetchMembershipCoupon();
  }, []);

  useEffect(() => {
    if (membership_id) {
      fetchMembershipDetails(membership_id);
    }
  }, [membership_id]);

  const handelBackStepper = () => {
    if (membership_id) {
      navigate(
        `/app/membership-setup/membership-creation?page_step=general_details&&membership_id=${membership_id}&&membership_applicable=${params.get("membership_applicable")}`,
      );
    } else {
      navigate(`/app/membership-setup/membership-creation?page_step=general_details&&membership_id=${membership_id}`);
    }
  };

  const onSubmit = (data: IMembershipAddCouponProps) => {
    // Enforce one-to-one mapping for exhaustible coupons
    const selectedExhaustible = data?.membership_coupons ?? [];
    const mappings = data?.coupon_voucher_mappings ?? [];
    if (selectedExhaustible.length > 0) {
      const allMapped =
        mappings.length === selectedExhaustible.length &&
        mappings.every((m) => !!m.voucher_family_id && !!m.coupon_id);
      if (!allMapped) {
        // Let RHF validation handle field-level errors; short-circuit submit
        return;
      }
    }

    // Build a quick lookup of voucher family by coupon_id
    const voucherByCouponId: Record<string, string | undefined> = {};
    mappings.forEach((m) => {
      voucherByCouponId[m.coupon_id] = m.voucher_family_id;
    });

    const constructedBody: IMembershipUpdateProps = {
      coupons_details: {
        exhaustible_coupons: data?.membership_coupons
          ? data?.membership_coupons?.map((exhaust) => ({
              coupon_id: exhaust?.coupon_id,
              coupon_name: exhaust?.coupon_name,
              voucher_family_id: voucherByCouponId[exhaust?.coupon_id],
            }))
          : [],
        other_coupons: data?.membership_other_coupons.map((other) => ({
          coupon_id: other?.coupon_id,
          coupon_name: other?.coupon_name,
        })),
      },
    };
    updateMembership(constructedBody, membership_id).then(({ status }) => {
      if (status) {
        navigate(
          `/app/membership-setup/membership-creation?page_step=membership_cms&&membership_id=${membership_id}&&membership_applicable=${params.get("membership_applicable")}`,
        );
      }
    });
  };

  useEffect(() => {
    if (membershipDetails) {
      if (membershipDetails?.coupons_details?.exhaustible_coupons?.length) {
        const selectedCoupons = membershipDetails?.coupons_details?.exhaustible_coupons.map((coupon) => ({
          coupon_id: coupon.coupon_id,
          coupon_name: coupon.coupon_name,
        }));
        formHook.setValue("membership_coupons", selectedCoupons);

        // Initialize mapping rows for each exhaustible coupon
        const initMappings = membershipDetails?.coupons_details?.exhaustible_coupons.map((coupon) => ({
          coupon_id: coupon.coupon_id,
          coupon_name: coupon.coupon_name,
          voucher_family_id: coupon.voucher_family_id,
        }));
        replaceMappings(initMappings);
      }
      if (membershipDetails?.coupons_details?.other_coupons?.length) {
        const selectedCoupons = membershipDetails?.coupons_details?.other_coupons.map((coupon) => ({
          coupon_id: coupon.coupon_id,
          coupon_name: coupon.coupon_name,
        }));
        formHook.setValue("membership_other_coupons", selectedCoupons);
      }
    }
  }, [membershipDetails, formHook, replaceMappings]);

  const { execute: fetchVoucherList } = useMembershipVoucherListingAPI();
  const [voucherFamiliesByServiceType, setVoucherFamiliesByServiceType] = useState<Record<string, any[]>>({});
  const VOUCHER_FALLBACK_KEY = "ALL";

  // Prefetch fallback and fetch voucher families for each selected coupon_type
  useEffect(() => {
    const selectedCoupons = formHook.watch("membership_coupons") ?? [];
    const allCoupons = membershipCouponList?.exhaustible_coupons ?? [];
    const neededTypes = Array.from(
      new Set(
        selectedCoupons
          .map((c) => allCoupons.find((ac) => ac.coupon_id === c.coupon_id)?.coupon_type)
          .filter((ct) => !!ct) as string[],
      ),
    );

    // Ensure fallback list exists
    (async () => {
      if (!voucherFamiliesByServiceType[VOUCHER_FALLBACK_KEY]) {
        const data = await fetchVoucherList();
        setVoucherFamiliesByServiceType((prev) => ({ ...prev, [VOUCHER_FALLBACK_KEY]: data ?? [] }));
      }
    })();

    neededTypes.forEach(async (serviceType) => {
      if (!voucherFamiliesByServiceType[serviceType]) {
        const data = await fetchVoucherList(serviceType);
        setVoucherFamiliesByServiceType((prev) => ({ ...prev, [serviceType]: data ?? [] }));
      }
    });
  }, [formHook.watch("membership_coupons"), membershipCouponList]);

  // Keep coupon-voucher mapping in sync with selected exhaustible coupons
  useEffect(() => {
    const selectedCoupons = formHook.watch("membership_coupons") ?? [];
    const existingMappings = formHook.getValues("coupon_voucher_mappings") ?? [];
    // Build new mapping list preserving existing voucher selections
    const newMappings = (selectedCoupons || []).map((c) => {
      const existing = existingMappings.find((m) => m.coupon_id === c.coupon_id);
      return {
        coupon_id: c.coupon_id,
        coupon_name: c.coupon_name,
        voucher_family_id: existing?.voucher_family_id,
      };
    });
    replaceMappings(newMappings);
  }, [formHook.watch("membership_coupons")]);

  return (
    <div>
      <form className="w-full mt-2" onSubmit={formHook.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4 w-full bg-white px-4 py-3 sm:px-6 lg:px-8">
          <h2 className="text-sm font-semibold">Coupons for Exhaustible/Key Services</h2>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>Coupons</p>
            </div>
            <div className="w-3/12">
              <SearchableMultiSelectMenu
                onSelectItem={(items) => {
                  const couponsId = items.map((i) => ({ coupon_id: i.id, coupon_name: i.title }));
                  formHook.setValue("membership_coupons", couponsId);
                  formHook.clearErrors("membership_coupons");

                  // Sync mapping rows with selected coupons
                  const existingMappings = formHook.getValues("coupon_voucher_mappings") ?? [];
                  const newMappings = couponsId.map((c) => {
                    const existing = existingMappings.find((m) => m.coupon_id === c.coupon_id);
                    return {
                      coupon_id: c.coupon_id,
                      coupon_name: c.coupon_name,
                      voucher_family_id: existing?.voucher_family_id,
                    };
                  });
                  replaceMappings(newMappings);
                }}
                errorMessages={[]}
                fieldError={formHook.formState.errors.membership_coupons}
                register={formHook.register("membership_coupons", {
                  required: false,
                })}
                withSelectAll
                showTooltips={true}
                showTypedErrors={false}
                placeholder="Select Coupons"
                defaultSelected={membershipExhaustibleCouponOptions.filter((c) =>
                  formHook.watch("membership_coupons")?.some((coupon) => coupon.coupon_id === c.id),
                )}
                selectItems={membershipExhaustibleCouponOptions}
              />
            </div>
          </div>
        </div>
        {mappingFields?.length > 0 && (
          <div className={`my-4 bg-white px-4 py-3 rounded-lg w-full ${mappingFields?.length > 3 && "h-[250px] overflow-auto"}`}>
            <h3 className="text-sm font-semibold mb-2">Voucher Family Mapping</h3>
            {mappingFields.map((field, index) => (
              <div key={field.id} className="grid grid-cols-5 items-center gap-4 py-3 border-b w-full">
                <div className="col-span-2 text-xs font-medium text-gray-700 truncate">
                  {formHook.watch(`coupon_voucher_mappings.${index}.coupon_name`)}
                </div>
                <div className="col-span-3">
                  {(() => {
                    const couponId = formHook.watch(`coupon_voucher_mappings.${index}.coupon_id`);
                    const allCoupons = membershipCouponList?.exhaustible_coupons ?? [];
                    const serviceType = allCoupons.find((ac) => ac.coupon_id === couponId)?.coupon_type;
                    const rowVoucherItems = reduceSelectMenu(
                      (voucherFamiliesByServiceType[serviceType] && voucherFamiliesByServiceType[serviceType].length > 0)
                        ? voucherFamiliesByServiceType[serviceType]
                        : voucherFamiliesByServiceType[VOUCHER_FALLBACK_KEY] ?? [],
                      "name",
                      "voucher_family_id",
                    );
                    return (
                  <SearchableSelectMenu
                     errorMessages={[{ message: "Voucher family is required", type: "required" }]}
                     onSelectItem={(item) => {
                       if (item) {
                         formHook.setValue(`coupon_voucher_mappings.${index}.voucher_family_id`, item.id);
                       } else {
                         formHook.setValue(`coupon_voucher_mappings.${index}.voucher_family_id`, undefined);
                       }
                     }}
                     fieldError={
                       formHook?.formState?.errors?.coupon_voucher_mappings &&
                       (formHook?.formState?.errors?.coupon_voucher_mappings as any)[index]?.voucher_family_id
                     }
                     register={formHook.register(`coupon_voucher_mappings.${index}.voucher_family_id`, {
                       required: true,
                     })}
                      selectItems={rowVoucherItems}
                     placeholder="Select Voucher Family"
                     showTooltips={true}
                     showTypedErrors={true}
                     showDropdownIcon={true}
                     defaultSelected={
                        rowVoucherItems?.find(
                          (oc) => oc.id === formHook.watch(`coupon_voucher_mappings.${index}.voucher_family_id`),
                        )
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800 w-full"
                      containerClassName="w-full"
                    />
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 flex flex-col gap-4 w-full bg-white px-4 py-3 sm:px-6 lg:px-8">
          <h2 className="text-sm font-semibold">Other Coupons</h2>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>
                Coupons <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <SearchableMultiSelectMenu
                onSelectItem={(items) => {
                  const couponsId = items.map((i) => ({ coupon_id: i.id, coupon_name: i.title }));
                  formHook.setValue("membership_other_coupons", couponsId);
                  formHook.clearErrors("membership_other_coupons");
                }}
                errorMessages={[{ message: "Coupon is required", type: "required" }]}
                fieldError={formHook.formState.errors.membership_other_coupons}
                register={formHook.register("membership_other_coupons", {
                  required: true,
                })}
                withSelectAll
                showTooltips={true}
                showTypedErrors={true}
                placeholder="Select Coupons"
                defaultSelected={membershipOtherCouponOptions.filter((c) =>
                  formHook.watch("membership_other_coupons")?.some((coupon) => coupon.coupon_id === c.id),
                )}
                selectItems={membershipOtherCouponOptions}
              />
            </div>
          </div>
        </div>
        <div className="right-5 bottom-0 fixed flex justify-end items-center gap-4 border-t-2 w-full p-3 bg-white">
          <p className="col-span-6"></p>
          <button
            type="button"
            onClick={() => handelBackStepper()}
            className="text-xs mt-3 inline-flex w-full justify-center rounded-md bg-[#b1b5bb] px-4 py-3 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="text-xs mt-3 inline-flex w-full justify-center rounded-md bg-[#193A6A] px-4 py-3 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
          >
            Save & Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default MembershipAddCouponComponent;
